import React, { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import { FiltersService } from '../../services/FiltersService';
import { DROPDOWN_CODES } from '../../constants/dropdown.constants';
import SimpleDropdown from './SimpleDropdown';

interface ProficiencyLevelDropdownProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: 'large' | 'middle' | 'small';
  onDropdownLoad?: () => void; // Callback when dropdown options are loaded
}

const ProficiencyLevelDropdown: React.FC<ProficiencyLevelDropdownProps> = ({
  value,
  onChange,
  placeholder = "Select Proficiency Level",
  disabled = false,
  size = 'large',
  onDropdownLoad
}) => {
  const [proficiencyOptions, setProficiencyOptions] = useState<{ key: string, label: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const onDropdownLoadRef = useRef(onDropdownLoad);

  useEffect(() => {
    onDropdownLoadRef.current = onDropdownLoad;
  }, [onDropdownLoad]);

  const loadProficiencyOptions = useCallback(async () => {
    setLoading(true);
    try {
      const proficiencyLevels = await FiltersService.getDropdownItems(DROPDOWN_CODES.ProficiencyLevel);
      setProficiencyOptions(proficiencyLevels.map(item => ({
        key: (item.value || '').trim().toUpperCase(),
        label: item.label || ''
      })));
      if (onDropdownLoadRef.current) onDropdownLoadRef.current();
    } catch (error) {
      console.error('Failed to load proficiency level options:', error);
      message.error('Failed to load proficiency level options');
      if (onDropdownLoadRef.current) onDropdownLoadRef.current();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProficiencyOptions();
  }, [loadProficiencyOptions]);

  const handleProficiencyChange = (proficiencyCode: string) => {
    onChange(proficiencyCode);
  };

  // DEBUG LOG for dropdown value debugging
  console.log('ProficiencyLevelDropdown value:', value, 'options:', proficiencyOptions);

  return (
    <SimpleDropdown
      options={proficiencyOptions}
      value={value}
      onChange={handleProficiencyChange}
      placeholder={loading ? "Loading..." : placeholder}
      disabled={disabled || loading}
      size={size}
    />
  );
};

export default ProficiencyLevelDropdown; 